import React, { useState } from 'react';
import styled from 'styled-components';
import { Users } from '@styled-icons/fa-solid/Users';
import { UserFriends } from '@styled-icons/fa-solid/UserFriends';
import DMConversationItem from './DMConversationItem';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px 8px 16px;
  border-bottom: 1px solid var(--clr-border, #333);
  gap: 8px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const FilterLabel = styled.span`
  font-size: 0.75rem;
  color: var(--clr-text-secondary, #aaa);
  font-weight: 500;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: ${(props) =>
    props.active ? 'var(--clr-purple)' : 'transparent'};
  color: ${(props) =>
    props.active ? 'white' : 'var(--clr-text-secondary, #aaa)'};
  border: 1px solid
    ${(props) =>
      props.active ? 'var(--clr-purple)' : 'var(--clr-border, #333)'};
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.active ? 'var(--clr-purple)' : 'rgba(255, 255, 255, 0.05)'};
    border-color: var(--clr-purple);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const NoFriendsText = styled.div`
  padding: 16px;
  color: var(--clr-text-secondary, #aaa);
  font-size: 0.9rem;
  text-align: center;
  font-style: italic;
`;

function DMConversationList({
  conversations,
  friends,
  selectedConversation,
  onConversationSelect,
  onStartConversation,
  onRemoveFriend,
  onViewProfile,
  sortByLastActive = false,
  onSortToggle,
}) {
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);

  // Create a unified list that combines friends with their conversation data
  const unifiedFriendsList = friends.map((friend) => {
    // Find if this friend has an active conversation
    const conversation = conversations.find(
      (conv) => conv.friend?.id === friend.id
    );

    return {
      ...friend,
      conversation: conversation || null,
      lastMessage:
        conversation?.lastMessage || friend.dm?.latestMessage || null,
      lastMessageAt:
        conversation?.lastMessageAt || friend.dm?.lastMessageAt || null,
      unreadCount: conversation?.unreadCount || 0,
      hasConversation: !!conversation || friend.dm?.hasConversation,
    };
  });

  // Filter friends if online-only mode is enabled
  const filteredFriendsList = showOnlineOnly
    ? unifiedFriendsList.filter((friend) => friend.isOnline)
    : unifiedFriendsList;

  // Sort the filtered list
  const sortedFriends = filteredFriendsList.sort((a, b) => {
    if (sortByLastActive) {
      // Sort by last active time
      const aLastActive = a.last_active_at;
      const bLastActive = b.last_active_at;

      // Online friends first
      if (a.isOnline && !b.isOnline) return -1;
      if (!a.isOnline && b.isOnline) return 1;

      // If both online or both offline, sort by last active time
      if (aLastActive && bLastActive) {
        return new Date(b.last_active_at) - new Date(a.last_active_at);
      }
      if (aLastActive && !bLastActive) return -1;
      if (!aLastActive && bLastActive) return 1;

      // Finally, sort alphabetically by name
      const nameA = a.nickname || 'Anon';
      const nameB = b.nickname || 'Anon';
      return nameA.localeCompare(nameB);
    } else {
      // Default sorting: Most recent message time first
      const aHasMessage = a.lastMessageAt;
      const bHasMessage = b.lastMessageAt;

      if (aHasMessage && !bHasMessage) return -1;
      if (!aHasMessage && bHasMessage) return 1;

      if (aHasMessage && bHasMessage) {
        return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
      }

      // Second priority: Online friends before offline (when not filtering)
      if (!showOnlineOnly) {
        if (a.isOnline && !b.isOnline) return -1;
        if (!a.isOnline && b.isOnline) return 1;
      }

      // Finally, sort alphabetically by name
      const nameA = a.nickname || 'Anon';
      const nameB = b.nickname || 'Anon';
      return nameA.localeCompare(nameB);
    }
  });

  const handleFriendClick = (friend) => {
    if (friend.conversation) {
      onConversationSelect(friend.conversation);
    } else {
      onStartConversation(friend);
    }
  };

  if (friends.length === 0) {
    return (
      <Container>
        <NoFriendsText>
          No friends yet. Add friends to start messaging!
        </NoFriendsText>
      </Container>
    );
  }

  return (
    <Container>
      <FilterContainer>
        <FilterGroup>
          <FilterLabel>View</FilterLabel>
          <FilterButton
            active={showOnlineOnly}
            onClick={() => setShowOnlineOnly(!showOnlineOnly)}
          >
            {showOnlineOnly ? <UserFriends /> : <Users />}
            {showOnlineOnly ? 'Online Only' : 'All Friends'}
          </FilterButton>
        </FilterGroup>

        {onSortToggle && (
          <FilterGroup>
            <FilterLabel>Sort by</FilterLabel>
            <FilterButton active={sortByLastActive} onClick={onSortToggle}>
              {sortByLastActive ? '📅 Last Active' : '💬 Messages'}
            </FilterButton>
          </FilterGroup>
        )}
      </FilterContainer>
      {sortedFriends.length === 0 ? (
        <NoFriendsText>
          {showOnlineOnly ? 'No friends online' : 'No friends yet'}
        </NoFriendsText>
      ) : (
        sortedFriends.map((friend) => (
          <DMConversationItem
            key={friend.id}
            friend={friend}
            conversation={friend.conversation}
            lastMessage={friend.lastMessage}
            lastMessageAt={friend.lastMessageAt}
            unreadCount={friend.unreadCount}
            isSelected={selectedConversation?.id === friend.conversation?.id}
            onClick={() => handleFriendClick(friend)}
            onRemoveFriend={() => onRemoveFriend(friend)}
            onViewProfile={onViewProfile}
          />
        ))
      )}
    </Container>
  );
}

export default DMConversationList;
